import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import TiltCard from "@/components/TiltCard";
import useSEO from "@/hooks/useSEO";

const SITE = process.env.REACT_APP_BACKEND_URL || "https://nan.nandev.cloud";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = ["All", "Web", "Mobile", "AI", "SaaS", "CRM"];

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects`, { params: { limit: 200 } });
        if (Array.isArray(data)) {
          setProjects(data);
        }
      } catch {
        setProjects([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useSEO({
    title: "Portfolio – Të gjitha projektet",
    description: "Shihni të gjitha projektet tona – website, aplikacione mobile, sisteme AI dhe platforma SaaS të ndërtuara nga NaN Studio.",
    url: `${SITE}/portfolio`,
  });

  const filtered =
    filter === "All"
      ? projects
      : projects.filter((p) =>
          (p.discipline || "").toLowerCase().includes(filter.toLowerCase())
        );

  return (
    <div data-testid="portfolio-page" className="bg-[#050505] text-white min-h-screen">
      <Nav />

      <main className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between mb-16 md:mb-24">
            <Link
              to="/"
              data-testid="portfolio-back-home"
              className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white"
            >
              <ArrowLeft size={14} /> Back home
            </Link>
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/40">
              {projects.length} projects
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
            <div className="md:col-span-4">
              <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                — Full Archive
              </div>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display font-extrabold uppercase leading-[0.85] tracking-tighter text-5xl md:text-7xl lg:text-[6rem]">
                Every<br />
                <span className="text-[#29ABE2]">project</span><br />
                we've shipped.
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-16 border-t border-b border-white/[0.06] py-6">
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mr-4">
              Filter
            </span>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`portfolio-filter-${f.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-xs font-mono-accent uppercase tracking-[0.2em] transition-colors ${
                  filter === f
                    ? "bg-[#29ABE2] text-black"
                    : "border border-white/[0.12] text-white/70 hover:text-white hover:border-white/40"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-white/40 text-sm py-20 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-white/40 text-sm py-20 text-center border border-white/[0.06]">
              No projects in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.06 }}
                  className="group"
                  data-testid={`portfolio-grid-item-${p.id}`}
                >
                  <Link to={`/portfolio/${p.slug || p.id}`}>
                    <TiltCard project={p} index={i} />
                  </Link>
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#29ABE2]">
                        {p.client}
                      </div>
                      <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40">
                        {p.year}
                      </div>
                    </div>
                    <Link to={`/portfolio/${p.slug || p.id}`}>
                      <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight hover:text-[#29ABE2] transition-colors">
                        {p.title}
                      </h3>
                    </Link>
                    <div className="text-white/50 text-xs uppercase tracking-[0.2em] font-mono-accent mt-1.5">
                      {p.discipline}
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed mt-3 line-clamp-3">
                      {p.description}
                    </p>
                    {p.case_url && (
                      <a
                        href={p.case_url}
                        target={p.case_url.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-5 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white border-b border-white/30 pb-0.5 hover:text-[#29ABE2] hover:border-[#29ABE2]"
                      >
                        Case study <ArrowUpRight size={12} />
                      </a>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
